import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import '../components/SystemActionsPage.css';

const BATCH_SIZE = 200;

const EMPTY_TOTALS = {
  productsScanned: 0,
  variantsScanned: 0,
  variantsUpdated: 0,
  variantsAlreadyCorrect: 0,
  variantsFailed: 0,
};

function ProductBackfillPage() {
  const { currentUser } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [failures, setFailures] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const stopRequestedRef = useRef(false);

  const runOneBatch = useCallback(async (cursor) => {
    const token = await currentUser.getIdToken();
    const response = await fetch('/api/v1/admin/products/unit-price-backfill/batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ cursor, batchSize: BATCH_SIZE }),
    });
    if (!response.ok) {
      const text = await response.text();
      let message = `Batch failed with status ${response.status}.`;
      try { message = JSON.parse(text).message || message; } catch (e) { /* leave default */ }
      throw new Error(message);
    }
    return response.json();
  }, [currentUser]);

  const handleStart = async () => {
    if (isRunning) return;
    setIsRunning(true);
    stopRequestedRef.current = false;
    setTotals(EMPTY_TOTALS);
    setFailures([]);
    setError('');
    setStatusMessage('Starting…');

    let cursor = null;
    let hasNextPage = true;
    const runningTotals = { ...EMPTY_TOTALS };
    const runningFailures = [];

    try {
      while (hasNextPage && !stopRequestedRef.current) {
        setStatusMessage(`Processing batch… (${runningTotals.productsScanned} products scanned so far)`);
        const batch = await runOneBatch(cursor);

        runningTotals.productsScanned += batch.productsScanned;
        runningTotals.variantsScanned += batch.variantsScanned;
        runningTotals.variantsUpdated += batch.variantsUpdated;
        runningTotals.variantsAlreadyCorrect += batch.variantsAlreadyCorrect;
        runningTotals.variantsFailed += batch.variantsFailed;
        if (batch.failures?.length) runningFailures.push(...batch.failures);

        setTotals({ ...runningTotals });
        setFailures([...runningFailures]);

        hasNextPage = batch.hasNextPage;
        cursor = batch.nextCursor;
      }
      setStatusMessage(stopRequestedRef.current ? 'Stopped by user.' : 'Done — full catalog scanned.');
    } catch (err) {
      setError(err.message || 'Backfill failed.');
      setStatusMessage('Stopped — error.');
    } finally {
      setIsRunning(false);
    }
  };

  const handleStop = () => {
    stopRequestedRef.current = true;
  };

  return (
    <div className="system-actions-container">
      <h1>Product Backfill</h1>
      <div className="admin-section">
        <h2>Unit Price Backfill</h2>
        <p>
          Sets Shopify's unit price (₹/meter) on fabric-by-length products (Digital Printed Fabric,
          Plain Dyed Fabric, Plain Dyed Pattern Fabric, Design Gallery — base SKU = per meter, SKU
          suffix like <code>-2.5M</code> = fixed cut length) and Fabric Set (always 5m). Everything
          else is left untouched. Safe to re-run — already-correct variants are skipped, so this
          also runs automatically every Monday 00:11 IST to pick up new products.
        </p>
        <div className="sync-actions">
          <button onClick={handleStart} disabled={isRunning} className="shopify-sync-btn">
            {isRunning ? 'Running…' : 'Run Unit Price Backfill'}
          </button>
          <button onClick={handleStop} disabled={!isRunning} className="zoho-sync-btn">
            Stop
          </button>
        </div>

        {statusMessage && <p className="sync-message success">{statusMessage}</p>}
        {error && <p className="sync-message error">{error}</p>}

        <div style={{ marginTop: 16 }}>
          <table className="history-table">
            <thead>
              <tr>
                <th>Products scanned</th>
                <th>Variants scanned</th>
                <th>Updated</th>
                <th>Already correct</th>
                <th>Failed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{totals.productsScanned}</td>
                <td>{totals.variantsScanned}</td>
                <td>{totals.variantsUpdated}</td>
                <td>{totals.variantsAlreadyCorrect}</td>
                <td>{totals.variantsFailed}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {failures.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <strong>Failures (needs manual review):</strong>
            <ul className="duplicate-sample-list">
              {failures.slice(0, 20).map((f, idx) => (
                <li key={`${f.productId}-${idx}`}>
                  <div><strong>Product:</strong> {f.productId}</div>
                  <div><strong>Error:</strong> {(f.errors || []).map(e => e.message).join('; ')}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductBackfillPage;
